import React from 'react';
import s from './s.module.less';
import { Button, Drawer, Input, Spin } from 'antd';
import { getTimeStr } from 'utils/calendar';
import { SearchOutlined } from '@ant-design/icons';
import { getConversations } from 'api/ai';
import dayjs from 'dayjs';
import { getAiApiData, getAiUserId, isAiApiSuccess } from 'utils/common';
import { TConversation } from 'types/ai';

type History = Record<string, TConversation[]>;

type Props = {
    onSearch: (val:string)=>void,
    onConversationUpdate: (id: string, name: string)=>void,
};

const HistoryButton = (prop:Props) => {
    const { onSearch, onConversationUpdate } = prop;
    const [showHistory, setShowHistory] = React.useState(false);
    const [searchFilter, setSearchFilter] = React.useState('');

    const [loading, setLoading] = React.useState(false);
    const [historyData, setHistoryData] = React.useState<History>({});

    const handleSearch = (val?:string) => {
        if (val) {
            onSearch?.(val);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        const result = await getConversations(getAiUserId(), 100);

        if (isAiApiSuccess(result)) {
            const data = getAiApiData(result);
            const _data:TConversation[] = data?.data || [];
            const _historyData:History = {};
            const historyTimeKeys:string[] = [];
            _data.forEach((item) => {
                const time = dayjs(item.created_at! * 1000).format('MMM DD, YYYY');
                if (!historyTimeKeys.includes(time)) {
                    historyTimeKeys.push(time);
                    _historyData[time] = [];
                }
                _historyData[time].push(item);
            });
            setHistoryData(_historyData);
        }
        setLoading(false);
    };

    React.useEffect(() => {
        if (showHistory) {
            // fetch history data
            fetchData();
        }
    }, [showHistory]);

    const dataAfterFilter:History = {};
    if (historyData) {
        Object.keys(historyData).forEach((key) => {
            if (!searchFilter) {
                dataAfterFilter[key] = historyData[key];
                return;
            }
            const datas = historyData[key];
            const filterDatas = datas?.filter((val) => val.name?.toLowerCase().includes(searchFilter.toLowerCase()));
            if (filterDatas && filterDatas.length > 0) {
                dataAfterFilter[key] = filterDatas;
            }
        });
    }

    return (
        <div className={s.wrap}>
            <Button
                onClick={() => {
                    setShowHistory(true);
                }}
                className={s.btn}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <g clipPath="url(#clip0_4573_69609)">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6.17661 0.0834209C6.15967 0.0844961 6.09415 0.0880801 6.03101 0.0913953C4.97958 0.146589 3.91686 0.43537 3.08261 0.892587C2.73108 1.08525 2.37015 1.33934 2.09701 1.58645C2.01796 1.65796 1.83733 1.83886 1.76428 1.91967C1.07862 2.67816 0.605153 3.66235 0.370149 4.81767C0.162485 5.83858 0.169328 6.95329 0.389525 7.97198C0.548173 8.70595 0.804838 9.36792 1.15757 9.95287C1.61671 10.7143 2.21118 11.2886 3.01821 11.7503C3.14248 11.8214 3.44337 11.972 3.57261 12.0277C4.28809 12.3363 5.05471 12.5238 5.86021 12.5872C6.10373 12.6064 6.19847 12.6098 6.48741 12.6097C6.81584 12.6097 7.02237 12.5997 7.29941 12.5706C8.49823 12.4445 9.59089 12.0497 10.4578 11.4292L10.525 11.3811L11.7402 12.5953C12.4086 13.2631 12.968 13.8184 12.9834 13.8293C13.0845 13.9007 13.1777 13.9274 13.3068 13.9218C13.3788 13.9187 13.4483 13.9026 13.4976 13.8776C13.5804 13.8357 13.6712 13.7497 13.7157 13.6709C13.8078 13.5081 13.8004 13.3009 13.697 13.1463C13.6872 13.1317 13.1362 12.5763 12.4725 11.9122L11.2658 10.7048L11.3232 10.6382C11.4756 10.4613 11.6468 10.2265 11.782 10.0089C11.9615 9.72006 12.1475 9.34411 12.276 9.01026C12.6589 8.01583 12.8181 6.8744 12.7312 5.74727C12.6217 4.32902 12.1435 3.05087 11.3683 2.10488C11.1661 1.85812 10.918 1.61187 10.6706 1.41244C9.96803 0.846057 9.06812 0.44223 8.03861 0.231345C7.70257 0.16251 7.3691 0.119115 6.95781 0.0906953C6.87824 0.0852017 6.24221 0.0792769 6.17661 0.0834209ZM6.30821 1.08327C5.16755 1.10857 4.06337 1.42487 3.25061 1.95912C2.57717 2.4018 2.08396 3.0032 1.72593 3.81827C1.51127 4.30693 1.35719 4.86888 1.27992 5.44487C1.21877 5.90068 1.20458 6.41206 1.24033 6.87184C1.31029 7.77179 1.55141 8.61424 1.93626 9.30327C2.28085 9.92024 2.74678 10.409 3.34301 10.7789C3.59647 10.9362 3.91114 11.0915 4.20271 11.2032C5.64315 11.7553 7.41095 11.7458 8.83381 11.1783C9.02524 11.102 9.27053 10.9866 9.42181 10.9018C10.1464 10.4955 10.65 9.9946 11.0486 9.28367C11.0952 9.20071 11.2131 8.96054 11.255 8.86331C11.8502 7.48356 11.9161 5.75787 11.4293 4.29558C11.1314 3.40101 10.6653 2.6945 10.0406 2.19095C9.58841 1.82643 9.02117 1.53924 8.37554 1.34795C7.71529 1.15233 7.04777 1.06687 6.30821 1.08327ZM6.38381 2.6814C6.31493 2.69553 6.24706 2.72533 6.19341 2.765C6.12495 2.81563 6.05308 2.90809 6.02072 2.98721C5.98367 3.07777 5.98591 2.95024 5.98777 4.8653C5.98934 6.46746 5.99019 6.60386 5.99883 6.63487C6.02394 6.72501 6.07263 6.80726 6.13828 6.87048C6.20781 6.93743 6.26685 6.97208 6.35493 6.99763L6.39781 7.01007H8.23741H10.077L10.1207 6.99742C10.2542 6.95872 10.3655 6.86783 10.4321 6.74309C10.502 6.61239 10.5054 6.43851 10.4408 6.30217C10.3753 6.16401 10.2463 6.05964 10.0966 6.02375C10.0568 6.01421 9.97454 6.01359 8.52161 6.01196L6.98861 6.01024L6.98858 4.57815C6.98854 3.04004 6.98941 3.08888 6.96082 3.00644C6.9158 2.87659 6.79539 2.75194 6.66899 2.70437C6.58716 2.67357 6.46853 2.66401 6.38381 2.6814Z" fill="black" />
                    </g>
                    <defs>
                        <clipPath id="clip0_4573_69609">
                            <rect width="14" height="14" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
                <span>History</span>
            </Button>

            <Drawer
                title="History"
                onClose={() => {
                    setShowHistory(false);
                }}
                open={showHistory}
                className={s.drawer}
            >
                <div className={s.drawerWrap}>
                    <div className={s.searchWrap}>
                        <Input
                            value={searchFilter}
                            onChange={((e) => {
                                setSearchFilter(e.target.value);
                            })}
                            placeholder="Search"
                            prefix={<SearchOutlined />}
                        />
                    </div>
                    {
                        loading &&
                        <Spin spinning className={s.loading} />
                    }
                    <div className={s.historyWrap}>
                        {
                            Object.keys(dataAfterFilter)?.map((val:string) => {
                                const historyItem:TConversation[] = dataAfterFilter[val];

                                return (
                                    <div
                                        className={s.historyItem}
                                        key={val}
                                    >
                                        <div className={s.historyTime}>
                                            {val}
                                        </div>
                                        <div className={s.historyContentWrap}>
                                            {
                                                historyItem?.map((hisVal:TConversation) => {
                                                    return (
                                                        <div
                                                            onClick={() => {
                                                                onConversationUpdate?.(hisVal.id, hisVal.name);
                                                                setShowHistory(false);
                                                            }}
                                                            className={s.historyContentItem}
                                                            key={hisVal.id}
                                                        >
                                                            {hisVal.name}
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default HistoryButton;
